import { createLogger } from "../utils/logger.js";
import { VIS_COLORS } from "./vis-colors.js";
import { CSS3DObject } from "three/addons/renderers/CSS3DRenderer.js";
import L from "leaflet";
//#region ../src/visualization/leaflet-map-overlay.ts
const log = createLogger("LeafletMapOverlay");
/** Default pixel size of the Leaflet map container */
const DEFAULT_LEAFLET_MAP_SIZE_PX = 600;
/** Default world-space size in meters (matches old MapOverlay) */
const DEFAULT_WORLD_SIZE = 10;
/** Default height offset below camera (negative = below) */
const DEFAULT_HEIGHT_OFFSET = -4;
/** Default forward offset from parent (negative = forward in parent-local Z) */
const DEFAULT_Z_OFFSET = -1;
/** Default zoom level for the Leaflet map */
const DEFAULT_ZOOM = 17;
/** Minimum allowed zoom level */
const MIN_ZOOM = 0;
/** Maximum allowed zoom level */
const MAX_ZOOM = 19;
var LeafletMapOverlay = class {
	scene;
	camera;
	mapParent;
	worldSize;
	heightOffset;
	mapSizePx;
	tileServerUrl;
	onTileError;
	offscreenRoot;
	zoomLevel;
	gpsPosition = null;
	visible = false;
	mapContainer = null;
	leafletMap = null;
	tileLayer = null;
	cssObject = null;
	rawGpsPoints = [];
	fusedPoints = [];
	snapshotPoints = [];
	namedMarkers = [];
	userMarker = null;
	rawGpsPolyline = null;
	fusedPolyline = null;
	snapshotPolyline = null;
	constructor(scene, camera, options = {}) {
		this.scene = scene;
		this.camera = camera;
		this.mapParent = options.mapParent ?? camera;
		this.worldSize = options.worldSize ?? 10;
		this.heightOffset = options.heightOffset ?? -4;
		this.mapSizePx = options.mapSizePx ?? 600;
		this.zoomLevel = options.zoomLevel ?? 17;
		this.tileServerUrl = options.tileServerUrl ?? "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
		this.onTileError = options.onTileError;
		this.offscreenRoot = options.offscreenRoot ?? document.body;
	}
	setGpsPosition(lat, lon) {
		this.gpsPosition = {
			lat,
			lon
		};
		if (this.leafletMap) {
			this.leafletMap.setView([lat, lon], this.zoomLevel);
			this.updateUserMarker();
		}
	}
	getGpsPosition() {
		return this.gpsPosition;
	}
	show() {
		if (!this.gpsPosition) {
			log.warn("Cannot show map — no GPS position set");
			return;
		}
		if (this.visible) return;
		this.createLeafletMap();
		this.createCssObject();
		this.applyBufferedOverlays();
		this.visible = true;
		log.info("Leaflet map overlay shown");
	}
	hide() {
		if (!this.visible) return;
		this.removeCssObject();
		this.visible = false;
		log.info("Leaflet map overlay hidden");
	}
	toggle() {
		if (this.visible) this.hide();
		else this.show();
	}
	isVisible() {
		return this.visible;
	}
	addRawGpsPoint(lat, lon) {
		this.rawGpsPoints.push([lat, lon]);
		if (this.leafletMap) {
			if (!this.rawGpsPolyline) this.rawGpsPolyline = L.polyline([], {
				color: VIS_COLORS.RAW_GPS.css,
				weight: 3,
				opacity: .8
			}).addTo(this.leafletMap);
			this.rawGpsPolyline.addLatLng([lat, lon]);
		}
	}
	addFusedPoint(lat, lon) {
		this.fusedPoints.push([lat, lon]);
		if (this.leafletMap) {
			if (!this.fusedPolyline) this.fusedPolyline = L.polyline([], {
				color: VIS_COLORS.FUSED_VIO.css,
				weight: 3,
				opacity: .8
			}).addTo(this.leafletMap);
			this.fusedPolyline.addLatLng([lat, lon]);
		}
	}
	addAlignmentSnapshot(lat, lon) {
		this.snapshotPoints.push([lat, lon]);
		if (this.leafletMap) {
			if (!this.snapshotPolyline) this.snapshotPolyline = L.polyline([], {
				color: VIS_COLORS.ALIGNMENT_SNAPSHOT.css,
				weight: 3,
				opacity: .8
			}).addTo(this.leafletMap);
			this.snapshotPolyline.addLatLng([lat, lon]);
		}
	}
	/** Add a "current" named marker (red). Buffered if map is not yet shown. */
	addCurrentMarker(lat, lon, name) {
		const marker = this.leafletMap ? this.createNamedMarker(lat, lon, name, false) : null;
		this.namedMarkers.push({
			lat,
			lng: lon,
			name,
			isPrior: false,
			marker
		});
	}
	/** Add a "prior" named marker (green, decorated). Buffered if not shown. */
	addPriorMarker(lat, lon, name) {
		const marker = this.leafletMap ? this.createNamedMarker(lat, lon, name, true) : null;
		this.namedMarkers.push({
			lat,
			lng: lon,
			name,
			isPrior: true,
			marker
		});
	}
	/** Bulk add prior markers. */
	addPriorMarkers(markers) {
		for (const m of markers) this.addPriorMarker(m.lat, m.lon, m.name);
	}
	/** Remove all prior markers; current markers are unaffected. */
	clearPriorMarkers() {
		this.namedMarkers = this.namedMarkers.filter((m) => {
			if (m.isPrior) {
				m.marker?.remove();
				return false;
			}
			return true;
		});
	}
	setZoomLevel(zoom) {
		this.zoomLevel = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
		if (this.leafletMap) this.leafletMap.setZoom(this.zoomLevel);
	}
	getZoomLevel() {
		return this.zoomLevel;
	}
	/** Increment zoom level by 1 (clamped at MAX_ZOOM). */
	zoomIn() {
		this.setZoomLevel(this.zoomLevel + 1);
	}
	/** Decrement zoom level by 1 (clamped at MIN_ZOOM). */
	zoomOut() {
		this.setZoomLevel(this.zoomLevel - 1);
	}
	getWorldSize() {
		return this.worldSize;
	}
	getHeightOffset() {
		return this.heightOffset;
	}
	getMapSizePx() {
		return this.mapSizePx;
	}
	/** Expose the Leaflet map instance for testing and advanced usage. */
	getLeafletMap() {
		return this.leafletMap;
	}
	/**
	* No-op — kept for backward compatibility with the frame-loop call
	* in main.ts. The CSS3DObject is a child of the parent, so Three.js
	* scene graph propagation handles positioning automatically.
	*/
	updatePosition() {}
	dispose() {
		this.hide();
		this.destroyLeafletMap();
		this.rawGpsPoints = [];
		this.fusedPoints = [];
		this.snapshotPoints = [];
		this.namedMarkers = [];
		this.cssObject = null;
		log.info("Leaflet map overlay disposed");
	}
	createLeafletMap() {
		if (this.leafletMap) return;
		this.mapContainer = document.createElement("div");
		this.mapContainer.style.width = `${this.mapSizePx}px`;
		this.mapContainer.style.height = `${this.mapSizePx}px`;
		this.mapContainer.style.background = "white";
		this.mapContainer.style.position = "fixed";
		this.mapContainer.style.left = "-9999px";
		this.mapContainer.style.top = "-9999px";
		this.mapContainer.style.pointerEvents = "none";
		this.offscreenRoot.appendChild(this.mapContainer);
		const pos = this.gpsPosition;
		this.leafletMap = L.map(this.mapContainer, {
			center: [pos.lat, pos.lon],
			zoom: this.zoomLevel,
			zoomControl: false,
			attributionControl: false
		});
		this.tileLayer = L.tileLayer(this.tileServerUrl, { maxZoom: MAX_ZOOM });
		this.tileLayer.on("tileerror", (e) => {
			log.error("Failed to load map tile:", e.error);
			this.onTileError?.(e.error);
		});
		this.tileLayer.addTo(this.leafletMap);
	}
	destroyLeafletMap() {
		this.userMarker?.remove();
		this.userMarker = null;
		this.rawGpsPolyline?.remove();
		this.rawGpsPolyline = null;
		this.fusedPolyline?.remove();
		this.fusedPolyline = null;
		this.snapshotPolyline?.remove();
		this.snapshotPolyline = null;
		for (const m of this.namedMarkers) {
			m.marker?.remove();
			m.marker = null;
		}
		if (this.leafletMap) {
			this.leafletMap.remove();
			this.leafletMap = null;
		}
		if (this.mapContainer?.parentElement) this.mapContainer.parentElement.removeChild(this.mapContainer);
		this.mapContainer = null;
		this.tileLayer = null;
	}
	createCssObject() {
		if (!this.mapContainer) return;
		if (this.cssObject) {
			this.mapParent.add(this.cssObject);
			return;
		}
		this.mapContainer.style.position = "";
		this.mapContainer.style.left = "";
		this.mapContainer.style.top = "";
		this.cssObject = new CSS3DObject(this.mapContainer);
		const scale = this.worldSize / this.mapSizePx;
		this.cssObject.scale.set(scale, scale, scale);
		this.cssObject.position.set(0, this.heightOffset, -1);
		this.cssObject.rotation.x = -Math.PI / 2;
		this.mapParent.add(this.cssObject);
	}
	removeCssObject() {
		if (this.cssObject) this.mapParent.remove(this.cssObject);
	}
	applyBufferedOverlays() {
		if (!this.leafletMap) return;
		this.updateUserMarker();
		if (this.rawGpsPoints.length > 0) this.rawGpsPolyline = L.polyline(this.rawGpsPoints, {
			color: VIS_COLORS.RAW_GPS.css,
			weight: 3,
			opacity: .8
		}).addTo(this.leafletMap);
		if (this.fusedPoints.length > 0) this.fusedPolyline = L.polyline(this.fusedPoints, {
			color: VIS_COLORS.FUSED_VIO.css,
			weight: 3,
			opacity: .8
		}).addTo(this.leafletMap);
		if (this.snapshotPoints.length > 0) this.snapshotPolyline = L.polyline(this.snapshotPoints, {
			color: VIS_COLORS.ALIGNMENT_SNAPSHOT.css,
			weight: 3,
			opacity: .8
		}).addTo(this.leafletMap);
		for (const m of this.namedMarkers) if (!m.marker) m.marker = this.createNamedMarker(m.lat, m.lng, m.name, m.isPrior);
	}
	updateUserMarker() {
		if (!this.gpsPosition || !this.leafletMap) return;
		if (this.userMarker) this.userMarker.setLatLng([this.gpsPosition.lat, this.gpsPosition.lon]);
		else this.userMarker = L.marker([this.gpsPosition.lat, this.gpsPosition.lon], { icon: L.divIcon({
			className: "",
			html: `<div style="background:${VIS_COLORS.USER_POSITION.css};width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 0 6px rgba(59,130,246,0.6);"></div>`,
			iconSize: [14, 14],
			iconAnchor: [7, 7]
		}) }).addTo(this.leafletMap);
	}
	createNamedMarker(lat, lon, name, isPrior) {
		const color = isPrior ? VIS_COLORS.PRIOR_REF_POINT.css : VIS_COLORS.CURRENT_REF_POINT.css;
		const opacity = isPrior ? "opacity:0.8;" : "";
		const label = isPrior ? `📌 ${name} (prior)` : name;
		return L.marker([lat, lon], { icon: L.divIcon({
			className: "",
			html: `<div style="background:${color};width:12px;height:12px;border-radius:50%;border:2px solid white;${opacity}"></div>`,
			iconSize: [12, 12],
			iconAnchor: [6, 6]
		}) }).bindPopup(label).addTo(this.leafletMap);
	}
};
//#endregion
export { DEFAULT_HEIGHT_OFFSET, DEFAULT_LEAFLET_MAP_SIZE_PX, DEFAULT_WORLD_SIZE, DEFAULT_ZOOM, DEFAULT_Z_OFFSET, LeafletMapOverlay };
